/**
 * Simple 3-channel preprocessor for YOLO models.
 *
 * Draws a letterboxed video frame to an OffscreenCanvas, then hands it to
 * ort.Tensor.fromImage() so the uint8->float32 conversion and NCHW transpose
 * run in ONNX Runtime's native path instead of a JS pixel loop.
 */

import * as ort from 'onnxruntime-web/all';

export class SimplePreprocessor {
  constructor(inputSize = 320) {
    this.inputSize = inputSize;
    this._letterboxParams = null;
    this._lastVideoWidth = 0;
    this._lastVideoHeight = 0;

    this.canvas = null;
    this.ctx = null;
  }

  async initialize() {
    this.canvas = new OffscreenCanvas(this.inputSize, this.inputSize);
    // No willReadFrequently — we hand the canvas off via createImageBitmap,
    // never call getImageData, so a GPU-backed canvas is faster for drawImage.
    this.ctx = this.canvas.getContext('2d');
    return true;
  }

  _computeLetterboxParams(videoWidth, videoHeight) {
    const scale = Math.min(this.inputSize / videoWidth, this.inputSize / videoHeight);
    const newWidth = Math.round(videoWidth * scale);
    const newHeight = Math.round(videoHeight * scale);
    const offsetX = Math.floor((this.inputSize - newWidth) / 2);
    const offsetY = Math.floor((this.inputSize - newHeight) / 2);

    return { scale, newWidth, newHeight, offsetX, offsetY };
  }

  _updateLetterboxParams(videoWidth, videoHeight) {
    if (videoWidth !== this._lastVideoWidth || videoHeight !== this._lastVideoHeight) {
      this._letterboxParams = this._computeLetterboxParams(videoWidth, videoHeight);
      this._lastVideoWidth = videoWidth;
      this._lastVideoHeight = videoHeight;
    }
  }

  /**
   * Preprocess a video frame to a 3-channel NCHW tensor normalized to [0,1].
   *
   * @param {HTMLVideoElement|HTMLCanvasElement} video
   * @returns {Promise<ort.Tensor>} float32 tensor shaped [1, 3, H, W]
   */
  async preprocess(video) {
    const videoWidth = video.videoWidth || video.width;
    const videoHeight = video.videoHeight || video.height;

    this._updateLetterboxParams(videoWidth, videoHeight);
    const { newWidth, newHeight, offsetX, offsetY } = this._letterboxParams;

    this.ctx.fillStyle = '#808080';
    this.ctx.fillRect(0, 0, this.inputSize, this.inputSize);
    this.ctx.drawImage(video, offsetX, offsetY, newWidth, newHeight);

    const bitmap = await createImageBitmap(this.canvas);
    try {
      return await ort.Tensor.fromImage(bitmap, {
        tensorLayout: 'NCHW',
        tensorFormat: 'RGB',
        dataType: 'float32'
      });
    } finally {
      bitmap.close();
    }
  }

  getLetterboxParams() {
    return this._letterboxParams;
  }

  resetBuffer() {
    // No temporal buffers to reset
  }

  async dispose() {
    this.canvas = null;
    this.ctx = null;
  }
}

export default SimplePreprocessor;
